import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import InputWithLabel from "../../components/form/input/InputWithLabel";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import { VehicleType, VehicleFare } from "../../types";

// Add this interface to track both file and preview
interface ImageFile {
  file: File | null;
  preview: string | null;
}

function parseOptionalFloat(value: string): number | null {
  if (value.trim() === "") return null;
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function parseOptionalInt(value: string): number | null {
  if (value.trim() === "") return null;
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

export default function VehicleTypeForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState<VehicleType>({
    id: "",
    name: "",
    description: "",
    remarks: "",
    status: true,
    seatCapacity: 0,
    largeSuitcases: 0,
    mediumSuitcases: 0,
    luggageId: "",
    luggageKg: 0,
    hasDisabledSeat: false,
    hasChildSeat: false,
    hasBoosterSeat: false,
    hasSpecialLuggageSpace: false,
    pricePercentage: 0,
    estimatedPerLiterMileage: 0,
    freeCancellation: false,
    freeWaitingMinutes: 0,
    imageName: "",
    fareList: []
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Replace the imagePreview state with this
  const [selectedImage, setSelectedImage] = useState<ImageFile>({
    file: null,
    preview: null
  });

  // Make fetchVehicleTypeData a useCallback
  const fetchVehicleTypeData = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`/vehicletypes/${id}`);
      const { fares, fareList, ...vehicleType } = response.data;
      setFormData({
        ...vehicleType,
        fareList: fareList ?? fares ?? [],
      });
    } catch (error) {
      setError("Failed to fetch vehicle type data");
      console.error("Error fetching vehicle type:", error);
    }
  }, [id]);

  // Update useEffect with proper dependencies
  useEffect(() => {
    if (isEditMode) {
      fetchVehicleTypeData();
    }
  }, [isEditMode, fetchVehicleTypeData]); // Add fetchVehicleTypeData to dependencies

  const buildSubmitPayload = () => {
    const imageName = selectedImage.file
      ? selectedImage.file.name.replace(/[^a-zA-Z0-9._-]/g, "_")
      : formData.imageName || null;

    return {
      name: formData.name?.trim() || formData.description?.trim() || null,
      description: formData.description || "",
      remarks: formData.remarks || null,
      status: Boolean(formData.status),
      seatCapacity: formData.seatCapacity ?? null,
      largeSuitcases: formData.largeSuitcases ?? null,
      mediumSuitcases: formData.mediumSuitcases ?? null,
      luggageId: formData.luggageId || null,
      luggageKg: formData.luggageKg ?? null,
      hasDisabledSeat: Boolean(formData.hasDisabledSeat),
      hasChildSeat: Boolean(formData.hasChildSeat),
      hasBoosterSeat: Boolean(formData.hasBoosterSeat),
      hasSpecialLuggageSpace: Boolean(formData.hasSpecialLuggageSpace),
      pricePercentage: formData.pricePercentage ?? null,
      estimatedPerLiterMileage: formData.estimatedPerLiterMileage ?? null,
      freeCancellation: Boolean(formData.freeCancellation),
      freeWaitingMinutes: formData.freeWaitingMinutes ?? null,
      imageName,
      fareList: formData.fareList
        .filter(
          (fare) =>
            fare.serialNo != null &&
            fare.uptoMiles != null &&
            fare.farePrice != null
        )
        .map((fare) => ({
          serialNo: fare.serialNo,
          uptoMiles: fare.uptoMiles,
          farePrice: fare.farePrice,
        })),
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const payload = buildSubmitPayload();

      if (isEditMode) {
        await axiosInstance.put(`/vehicletypes/${id}`, payload);
      } else {
        await axiosInstance.post("/vehicletypes", payload);
      }
      navigate("/vehicle-type");
    } catch (error: any) {
      setError(error.response?.data?.message || "Failed to save vehicle type");
      console.error("Error saving vehicle type:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : 
              type === 'number' ? (value === '' ? undefined : Number(value)) : 
              value
    }));
  };

  const handleAddFare = () => {
    setFormData(prev => ({
      ...prev,
      fareList: [
        ...prev.fareList,
        {
          id: "",
          vehicleTypeId: prev.id,
          serialNo: prev.fareList.length + 1,
          uptoMiles: null,
          farePrice: null,
        }
      ]
    }));
  };

  const handleFareChange = (
    index: number,
    field: keyof VehicleFare,
    value: string
  ) => {
    setFormData(prev => ({
      ...prev,
      fareList: prev.fareList.map((fare, i) => {
        if (i !== index) return fare;
        if (field === "serialNo") {
          return { ...fare, serialNo: parseOptionalInt(value) ?? fare.serialNo };
        }
        if (field === "uptoMiles") {
          return { ...fare, uptoMiles: parseOptionalFloat(value) };
        }
        return { ...fare, farePrice: parseOptionalFloat(value) };
      })
    }));
  };

  const handleRemoveFare = (index: number) => {
    setFormData(prev => ({
      ...prev,
      fareList: prev.fareList.filter((_, i) => i !== index)
    }));
  };

  // Simplify handleImageChange
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Cleanup previous preview if it exists
      if (selectedImage.preview) {
        URL.revokeObjectURL(selectedImage.preview);
      }
      
      setSelectedImage({
        file,
        preview: URL.createObjectURL(file)
      });
    }
  };

  // Simplify handleRemoveImage
  const handleRemoveImage = () => {
    if (selectedImage.preview) {
      URL.revokeObjectURL(selectedImage.preview);
    }
    setSelectedImage({ file: null, preview: null });
    setFormData(prev => ({ ...prev, imageName: "" }));
  };

  const existingImageSrc =
    formData.imageName && !selectedImage.preview
      ? `/images/vehicle/${formData.imageName}`
      : null;
  const displayImageSrc = selectedImage.preview ?? existingImageSrc;

  return (
    <div className="w-full space-y-6">
      <PageBreadcrumb pageTitle={isEditMode ? "Edit Vehicle Type" : "Add Vehicle Type"} />

      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] lg:p-6">
        {error && (
          <div className="mb-4 rounded-xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700 dark:border-error-500/30 dark:bg-error-500/10 dark:text-error-400">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="w-full space-y-6">
          <div className="grid w-full grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
            {/* Basic Information */}
            <div className="col-span-2">
              <h3 className="mb-4 text-base font-semibold text-gray-900 dark:text-white/90">Basic Information</h3>
              <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                <InputWithLabel
                  label="Name"
                  name="name"
                  value={formData.name || ""}
                  onChange={handleInputChange}
                  required
                />
                <InputWithLabel
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                />
                <InputWithLabel
                  label="Remarks"
                  name="remarks"
                  value={formData.remarks || ""}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            {/* Vehicle Image */}
            <div className="col-span-2">
              <h3 className="mb-4 text-base font-semibold text-gray-900 dark:text-white/90">Vehicle Image</h3>
              <div className="flex items-start gap-4">
                <div className="flex-1 space-y-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                    id="vehicle-image"
                  />
                  <label
                    htmlFor="vehicle-image"
                    className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
                  >
                    {displayImageSrc ? "Change Image" : "Choose Image"}
                  </label>
                  {formData.imageName && !selectedImage.file && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Current file: {formData.imageName}
                    </p>
                  )}
                  {selectedImage.file && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Will save as: {selectedImage.file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    The image filename is stored on the vehicle type. Place the file in the site&apos;s{" "}
                    <code className="text-gray-600 dark:text-gray-300">/images/vehicle/</code> folder using that name.
                  </p>
                </div>
                {displayImageSrc && (
                  <div className="relative shrink-0">
                    <div className="h-32 w-32 overflow-hidden rounded-lg border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900">
                      <img
                        src={displayImageSrc}
                        alt="Vehicle"
                        className="h-full w-full object-contain"
                        onError={(e) => {
                          if (!e.currentTarget.src.includes("placeholder-image.png")) {
                            e.currentTarget.src = "/images/placeholder-image.png";
                          }
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute -top-2 -right-2 rounded-full bg-red-500 p-1 text-white hover:bg-red-600"
                      aria-label="Remove image"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Capacity & Luggage */}
            <div className="col-span-2">
              <h3 className="mb-4 text-base font-semibold text-gray-900 dark:text-white/90">Capacity & Luggage</h3>
              <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                <InputWithLabel
                  label="Seat Capacity"
                  name="seatCapacity"
                  type="number"
                  value={formData.seatCapacity?.toString() || ""}
                  onChange={handleInputChange}
                />
                <InputWithLabel
                  label="Large Suitcases"
                  name="largeSuitcases"
                  type="number"
                  value={formData.largeSuitcases?.toString() || ""}
                  onChange={handleInputChange}
                />
                <InputWithLabel
                  label="Medium Suitcases"
                  name="mediumSuitcases"
                  type="number"
                  value={formData.mediumSuitcases?.toString() || ""}
                  onChange={handleInputChange}
                />
                <InputWithLabel
                  label="Luggage Weight (kg)"
                  name="luggageKg"
                  type="number"
                  step="0.01"
                  value={formData.luggageKg?.toString() || ""}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            {/* Features */}
            <div className="col-span-2">
              <h3 className="mb-4 text-base font-semibold text-gray-900 dark:text-white/90">Features</h3>
              <div className="grid w-full grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="hasDisabledSeat"
                    checked={formData.hasDisabledSeat}
                    onChange={handleInputChange}
                    className="rounded border-gray-300"
                  />
                  <span>Disabled Seat</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="hasChildSeat"
                    checked={formData.hasChildSeat}
                    onChange={handleInputChange}
                    className="rounded border-gray-300"
                  />
                  <span>Child Seat</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="hasBoosterSeat"
                    checked={formData.hasBoosterSeat}
                    onChange={handleInputChange}
                    className="rounded border-gray-300"
                  />
                  <span>Booster Seat</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="hasSpecialLuggageSpace"
                    checked={formData.hasSpecialLuggageSpace}
                    onChange={handleInputChange}
                    className="rounded border-gray-300"
                  />
                  <span>Special Luggage Space</span>
                </label>
              </div>
            </div>

            {/* Pricing & Service */}
            <div className="col-span-2">
              <h3 className="mb-4 text-base font-semibold text-gray-900 dark:text-white/90">Pricing & Service</h3>
              <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                <InputWithLabel
                  label="Price Percentage"
                  name="pricePercentage"
                  type="number"
                  step="0.01"
                  value={formData.pricePercentage?.toString() || ""}
                  onChange={handleInputChange}
                />
                <InputWithLabel
                  label="Estimated MPG"
                  name="estimatedPerLiterMileage"
                  type="number"
                  step="0.01"
                  value={formData.estimatedPerLiterMileage?.toString() || ""}
                  onChange={handleInputChange}
                />
                <InputWithLabel
                  label="Free Waiting Minutes"
                  name="freeWaitingMinutes"
                  type="number"
                  value={formData.freeWaitingMinutes?.toString() || ""}
                  onChange={handleInputChange}
                />
                <div className="flex items-center space-x-4">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      name="freeCancellation"
                      checked={formData.freeCancellation}
                      onChange={handleInputChange}
                      className="rounded border-gray-300"
                    />
                    <span>Free Cancellation</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      name="status"
                      checked={formData.status}
                      onChange={handleInputChange}
                      className="rounded border-gray-300"
                    />
                    <span>Active</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Fare List */}
            <div className="col-span-2">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Fare List</h3>
                <button
                  type="button"
                  onClick={handleAddFare}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Add Fare
                </button>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800 dark:divide-gray-800">
                  <thead className="bg-gray-50 dark:bg-white/[0.02]">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Serial No</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Up to Miles</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Rate per Mile (£)</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-800 dark:bg-transparent">
                    {formData.fareList.map((fare, index) => (
                      <tr key={index}>
                        <td className="px-4 py-2">
                          <input
                            type="number"
                            min={1}
                            className="w-20 rounded-md border-gray-300"
                            value={fare.serialNo ?? ""}
                            onChange={(e) => handleFareChange(index, "serialNo", e.target.value)}
                          />
                        </td>
                        <td className="px-4 py-2">
                          <input
                            type="number"
                            min={0}
                            step="0.01"
                            placeholder="Miles"
                            className="w-28 rounded-md border-gray-300"
                            value={fare.uptoMiles ?? ""}
                            onChange={(e) => handleFareChange(index, "uptoMiles", e.target.value)}
                          />
                        </td>
                        <td className="px-4 py-2">
                          <input
                            type="number"
                            min={0}
                            step="0.01"
                            placeholder="£/mile"
                            className="w-28 rounded-md border-gray-300"
                            value={fare.farePrice ?? ""}
                            onChange={(e) => handleFareChange(index, "farePrice", e.target.value)}
                          />
                        </td>
                        <td className="px-4 py-2 text-right">
                          <button
                            type="button"
                            onClick={() => handleRemoveFare(index)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate("/vehicle-type")}
              className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600 disabled:opacity-50"
            >
              {loading ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 